import { Button } from '@strata-monorepo/shared-ui/components/atoms/Button'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'
import { useStoreState } from '../../store/useStore'
import { getLogoFromDomain } from '../../utils/getLogoFromDomain'
import { CallCTA } from './CallCTA'
import useElementClicked from '@strata-monorepo/tracking/src/hooks/useElementClicked'
import {
  ActionOutcome,
  CustomDimensionsKeys,
  ElementType,
} from '@strata-monorepo/tracking/src/types'

export function QuestionnaireHeader({
  onSkipToResults,
  isLastQuestion,
}: {
  onSkipToResults?: () => void
  isLastQuestion?: boolean
}) {
  const { siteDomain, siteName } = useStoreState((state) => state.siteConfig)
  const formId = useStoreState((state) => state.form.formId)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const isSkipToResultsEnabled = useStoreState((state) => state.form.isSkipToResultsEnabled)
  const trackElementClicked = useElementClicked()

  // Get logo components based on domain and variant
  const LogoMark = getLogoFromDomain(siteDomain, 'mark')
  const FullLogo = getLogoFromDomain(siteDomain, 'full')
  const showTakeMeToPlans = isSkipToResultsEnabled && !isLastQuestion

  const handleSkipToPlansClick = () => {
    trackElementClicked({
      clickType: ActionOutcome.Skip,
      itemName: 'take_me_to_plans',
      elementType: ElementType.Button,
      location: 'QUESTIONNAIRE HEADER',
      itemTitle: 'Take Me to Plans',
      itemPosition: '',
      customDimensions: [
        { key: CustomDimensionsKeys.FORM_ID, value: formId },
        { key: CustomDimensionsKeys.STEP_ID, value: `step-${currentStepIndex}` },
      ],
    })
    onSkipToResults?.()
  }

  return (
    <div className='strata:w-full strata:bg-secondary-50'>
      <div className='strata:@container/qc strata:max-w-[584px] strata:w-full strata:mx-auto'>
        <div
          className={cn(
            'strata:relative strata:flex strata:w-full strata:items-center strata:justify-between strata:gap-4',
            'strata:py-5 strata:min-h-18 strata:px-6 strata:@qm/qc:px-0',
          )}
        >
          {/* Left block: logo mark (mobile only) + phone CTA */}
          <div className='strata:relative strata:z-10 strata:flex strata:shrink-0 strata:items-center strata:gap-2'>
            <div className='strata:flex strata:h-8 strata:items-center strata:@qm/qc:hidden'>
              {LogoMark ? <LogoMark className='strata:h-8' /> : <h2>{siteName}</h2>}
            </div>
            <CallCTA
              trackingLocation='QUESTIONNAIRE_HEADER'
              trackingName='header_call_cta'
              trackingPosition='header'
            />
          </div>
          {/* Center block: full logo (tablet/desktop only) */}
          <div className='strata:absolute strata:left-1/2 strata:top-1/2 strata:-translate-x-1/2 strata:-translate-y-1/2 strata:z-0 strata:hidden strata:h-8 strata:items-center strata:@qm/qc:flex'>
            {FullLogo ? <FullLogo className='strata:h-8' /> : <h2>{siteName}</h2>}
          </div>

          {/* Right block: skip to results */}
          {showTakeMeToPlans ? (
            <div className='strata:relative strata:z-10 strata:shrink-0 strata:ml-auto'>
              <Button
                type='button'
                variant='tertiary'
                onClick={handleSkipToPlansClick}
                className='strata:border strata:border-solid strata:border-secondary-500 strata:bg-transparent strata:text-secondary-500 strata:hover:bg-secondary-500 strata:hover:text-white strata:py-2 strata:px-4 strata:text-body-sm strata:font-bold strata:rounded-sm'
              >
                Take Me to Plans
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
